import { GROUP_TYPES } from '../schemas/defaultGroups';

// Family Account Template - Defines the initial structure for new family accounts
export const FAMILY_ACCOUNT_TEMPLATE = {
  // Core family structure
  structure: {
    owner: {
      role: 'OWNER',
      defaultGroups: ['ADMIN', 'IMMEDIATE_FAMILY', 'PARENTS'],
      permissions: GROUP_TYPES.ADMIN.permissions
    },
    spouse: {
      role: 'SPOUSE',
      defaultGroups: ['IMMEDIATE_FAMILY', 'PARENTS'],
      permissions: GROUP_TYPES.IMMEDIATE_FAMILY.permissions
    },
    children: {
      role: 'CHILD',
      defaultGroups: ['IMMEDIATE_FAMILY', 'SIBLING_CHILDREN'],
      permissions: GROUP_TYPES.SIBLING_CHILDREN.permissions
    },
    elders: {
      role: 'ELDER',
      defaultGroups: ['ELDERS', 'EXTENDED_FAMILY'],
      permissions: GROUP_TYPES.ELDERS.permissions
    }
  },

  // Default session states
  sessionStates: {
    DEFAULT: {
      visibleGroups: ['IMMEDIATE_FAMILY', 'EXTENDED_FAMILY'],
      managedGroups: [],
      defaultActiveGroup: 'IMMEDIATE_FAMILY'
    },
    OWNER: {
      visibleGroups: ['ADMIN', 'IMMEDIATE_FAMILY', 'PARENTS', 'ELDERS', 'EXTENDED_FAMILY', 'PARENTS_SIBLINGS', 'SIBLING_CHILDREN', 'DISTANT_FAMILY'],
      managedGroups: ['ADMIN', 'IMMEDIATE_FAMILY', 'PARENTS', 'ELDERS', 'EXTENDED_FAMILY', 'PARENTS_SIBLINGS', 'SIBLING_CHILDREN', 'DISTANT_FAMILY'],
      defaultActiveGroup: 'IMMEDIATE_FAMILY'
    },
    SPOUSE: {
      visibleGroups: ['IMMEDIATE_FAMILY', 'PARENTS', 'ELDERS', 'EXTENDED_FAMILY', 'PARENTS_SIBLINGS', 'SIBLING_CHILDREN'],
      managedGroups: ['IMMEDIATE_FAMILY', 'PARENTS'],
      defaultActiveGroup: 'IMMEDIATE_FAMILY'
    },
    CHILD: {
      visibleGroups: ['IMMEDIATE_FAMILY', 'SIBLING_CHILDREN', 'EXTENDED_FAMILY'],
      managedGroups: [],
      defaultActiveGroup: 'IMMEDIATE_FAMILY'
    },
    ELDER: {
      visibleGroups: ['ELDERS', 'IMMEDIATE_FAMILY', 'EXTENDED_FAMILY'],
      managedGroups: [],
      defaultActiveGroup: 'ELDERS'
    }
  },

  // Feature access by role
  featureAccess: {
    OWNER: {
      canInviteMembers: true,
      canManageGroups: true,
      canManageVault: true,
      canManageSettings: true,
      canManagePermissions: true
    },
    SPOUSE: {
      canInviteMembers: true,
      canManageGroups: ['IMMEDIATE_FAMILY', 'PARENTS'],
      canManageVault: true,
      canManageSettings: false,
      canManagePermissions: false
    },
    CHILD: {
      canInviteMembers: false,
      canManageGroups: [],
      canManageVault: false,
      canManageSettings: false,
      canManagePermissions: false
    },
    ELDER: {
      canInviteMembers: false,
      canManageGroups: [],
      canManageVault: false,
      canManageSettings: false,
      canManagePermissions: false
    }
  }
};

// Session Manager - Handles active group sessions
export class FamilySessionManager {
  constructor(userId, familyCode, userRole) {
    this.userId = userId;
    this.familyCode = familyCode;
    this.userRole = userRole;
    
    // Use the role's session state if it exists, otherwise use DEFAULT
    this.sessionState = FAMILY_ACCOUNT_TEMPLATE.sessionStates[userRole] || FAMILY_ACCOUNT_TEMPLATE.sessionStates.DEFAULT;
    
    // Set active group from session state
    this.activeGroup = this.sessionState.defaultActiveGroup;
  }

  // Get visible groups for current user
  getVisibleGroups() {
    return this.sessionState.visibleGroups;
  }

  // Get manageable groups for current user
  getManageableGroups() {
    return this.sessionState.managedGroups;
  }

  // Set active group
  setActiveGroup(groupType) {
    if (this.sessionState.visibleGroups.includes(groupType)) {
      this.activeGroup = groupType;
      return true;
    }
    return false;
  }

  // Get current active group
  getActiveGroup() {
    return this.activeGroup;
  }

  // Check if user can perform action
  canPerformAction(action) {
    const featureAccess = FAMILY_ACCOUNT_TEMPLATE.featureAccess[this.userRole];
    return featureAccess[action] || false;
  }

  // Get user's feature access
  getFeatureAccess() {
    return FAMILY_ACCOUNT_TEMPLATE.featureAccess[this.userRole];
  }
}

// Family Account Manager - Handles family account initialization and management
export class FamilyAccountManager {
  constructor(familyCode) {
    this.familyCode = familyCode;
    this.template = FAMILY_ACCOUNT_TEMPLATE;
  }

  // Initialize new family account
  async initializeNewFamily(ownerUserId) {
    const structure = {
      familyCode: this.familyCode,
      owner: ownerUserId,
      members: [ownerUserId],
      groups: this.template.structure.owner.defaultGroups,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    return structure;
  }

  // Get role template
  getRoleTemplate(role) {
    return this.template.structure[role.toLowerCase()];
  }

  // Get default groups for role
  getDefaultGroupsForRole(role) {
    const roleTemplate = this.getRoleTemplate(role);
    return roleTemplate ? roleTemplate.defaultGroups : [];
  }

  // Get permissions for role
  getPermissionsForRole(role) {
    const roleTemplate = this.getRoleTemplate(role);
    return roleTemplate ? roleTemplate.permissions : null;
  }
}

// Export template constants
export const FAMILY_ROLES = {
  OWNER: 'OWNER',
  SPOUSE: 'SPOUSE',
  CHILD: 'CHILD',
  ELDER: 'ELDER'
};

export const SESSION_ACTIONS = {
  INVITE_MEMBERS: 'canInviteMembers',
  MANAGE_GROUPS: 'canManageGroups',
  MANAGE_VAULT: 'canManageVault',
  MANAGE_SETTINGS: 'canManageSettings',
  MANAGE_PERMISSIONS: 'canManagePermissions'
}; 